import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import { faXmark, faMagnifyingGlass, faSpinner, faPlus } from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import { decideTask } from '../../utils/taskApproval';
import ChatTaskCard from './ChatTaskCard';
import ChatTaskDrawer from './ChatTaskDrawer';

/**
 * The Tasks panel beside a direct chat: the other person's tasks, or for a
 * plain employee only the work running between the two of them. The server
 * decides which (GET /tasks/with/:userId) and says so in `scope`.
 *
 * A side panel on a wide screen, a bottom sheet on a phone (messenger.css), so
 * the conversation stays in view behind it.
 *
 * Filtering, search and sorting happen on the server, a page at a time; the
 * next page loads as the list is scrolled to its end. Every response carries
 * the per-status counts for the chips, and those go back up through onCounts
 * so the badge on the chat header stays in step.
 *
 * A card opens the task drawer. Self-assigned work waiting on this viewer
 * also gets Approve on the card and Approve / Reject in the drawer, and the
 * Awaiting approval chip appears whenever there is any.
 *
 * "Assign task" opens the Add Task page with this person already picked. It is
 * only offered when the viewer may assign to them (scope 'person': Admin, HR,
 * Managers, or their Team Lead), and the server checks that again on save.
 */

// `key` is what the server's status filter takes; `count` which count it shows.
const FILTERS = [
    { key: 'open', label: 'Open', count: 'open' },
    // Only shown while something is waiting - see the chip row below.
    { key: 'approval', label: 'Awaiting approval', count: 'approval' },
    { key: 'Pending', label: 'Pending', count: 'Pending' },
    { key: 'In Progress', label: 'In Progress', count: 'In Progress' },
    { key: 'On Hold', label: 'On Hold', count: 'On Hold' },
    { key: 'Completed', label: 'Completed', count: 'Completed' },
    { key: 'all', label: 'All', count: 'all' }
];

const SORTS = [
    { key: 'due', label: 'Due date' },
    { key: 'priority', label: 'Priority' }
];

// How close to the end of the list, in px, before the next page is fetched.
const LOAD_AHEAD = 160;

const ChatTasksPanel = ({ personId, name, myRole, onClose, onCounts }) => {
    const navigate = useNavigate();
    const [filter, setFilter] = useState('open');
    const [sort, setSort] = useState('due');
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState('');
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState(null);
    const [scope, setScope] = useState('person');
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openTask, setOpenTask] = useState(null);
    const [deciding, setDeciding] = useState(null);
    // Drops a slow page for a filter, sort or search the person has already left.
    const requestId = useRef(0);

    // Escape closes the panel - unless the drawer is over it, which takes it first.
    useEffect(() => {
        if (openTask) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [openTask, onClose]);

    useEffect(() => {
        const t = setTimeout(() => setSearch(query.trim()), 300);
        return () => clearTimeout(t);
    }, [query]);

    const load = useCallback(async (nextPage) => {
        const id = ++requestId.current;
        setLoading(true);
        setError('');
        try {
            const res = await api.get(`/tasks/with/${personId}`, {
                params: { status: filter, sort, search: search || undefined, page: nextPage }
            });
            if (id !== requestId.current) return;
            setItems((prev) => (nextPage === 1 ? res.data.items : [...prev, ...res.data.items]));
            setHasMore(res.data.hasMore);
            setPage(nextPage);
            setCounts(res.data.counts);
            setScope(res.data.scope);
            // Searched counts describe the search, not the chat - not for the badge.
            if (!search) onCounts?.(res.data.counts);
        } catch (err) {
            if (id === requestId.current) setError(err.response?.data?.message || 'Could not load tasks.');
        } finally {
            if (id === requestId.current) setLoading(false);
        }
    }, [personId, filter, sort, search, onCounts]);

    // A new filter, sort or search starts again from the top.
    const listRef = useRef(null);
    useEffect(() => {
        setItems([]);
        setHasMore(false);
        if (listRef.current) listRef.current.scrollTop = 0;
        load(1);
    }, [load]);

    const loadMore = () => {
        if (!loading && hasMore && !error) load(page + 1);
    };

    const onScroll = (e) => {
        const el = e.currentTarget;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_AHEAD) loadMore();
    };

    /*
     * A decision, taken here or in the drawer: the card stops offering it at
     * once and the chip count drops, without reloading the page of cards the
     * person has scrolled through. Under Awaiting approval the card leaves.
     */
    const decided = (taskId, decision) => {
        setItems((prev) => (filter === 'approval'
            ? prev.filter((t) => t._id !== taskId)
            : prev.map((t) => (t._id === taskId ? { ...t, approvalStatus: decision, canApprove: false } : t))));
        setCounts((c) => {
            if (!c) return c;
            // A rejected request leaves the panel's lists altogether.
            const gone = decision === 'Rejected' ? 1 : 0;
            const task = items.find((t) => t._id === taskId);
            const next = { ...c, approval: Math.max(0, c.approval - 1), all: c.all - gone };
            if (gone && task) {
                next[task.status] = Math.max(0, (next[task.status] || 0) - 1);
                if (task.status !== 'Completed') next.open = Math.max(0, next.open - 1);
            }
            onCounts?.(next);
            return next;
        });
    };

    /*
     * The drawer shows the discussion, and showing it marks it read on the
     * server, so the card's unread badge goes when the drawer does.
     */
    const read = (task) => {
        if (!task?.unreadComments) return;
        setItems((prev) => prev.map((t) => (t._id === task._id ? { ...t, unreadComments: 0 } : t)));
        setCounts((c) => {
            if (!c) return c;
            const next = { ...c, unread: Math.max(0, (c.unread || 0) - 1) };
            onCounts?.(next);
            return next;
        });
    };

    const approve = async (task) => {
        setDeciding(task._id);
        const done = await decideTask(task._id, 'Approved', name);
        setDeciding(null);
        if (done) decided(task._id, done.decision);
    };

    const total = counts ? counts[FILTERS.find((f) => f.key === filter).count] : null;
    const chips = FILTERS.filter((f) => f.key !== 'approval' || counts?.approval > 0 || filter === 'approval');

    return (
        <>
            <div className="msgr-tasks-backdrop" onClick={onClose} />
            <aside className="msgr-tasks" aria-label={`${name}'s tasks`}>
                <div className="msgr-sheet-grip" aria-hidden="true" />
                <div className="msgr-tasks-head">
                    <h4>Tasks <span>· {name}</span></h4>
                    {scope === 'person' && (
                        <button
                            type="button"
                            className="msgr-btn msgr-tasks-new"
                            onClick={() => navigate('/add-task', { state: { followUp: { assigneeIds: [personId] } } })}
                            title={`Assign a new task to ${name}`}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Assign task
                        </button>
                    )}
                    <button className="msgr-icon-btn" onClick={onClose} title="Close" aria-label="Close tasks">
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>

                <div className="msgr-tasks-search">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search title, project, priority…"
                        aria-label="Search tasks"
                        maxLength={80}
                    />
                    {query && (
                        <button type="button" onClick={() => setQuery('')} aria-label="Clear search">
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </div>

                <div className="msgr-tasks-filters" role="tablist">
                    {chips.map((f) => {
                        const n = counts?.[f.count];
                        return (
                            <button
                                key={f.key}
                                type="button"
                                role="tab"
                                aria-selected={filter === f.key}
                                className={`msgr-chip ${filter === f.key ? 'active' : ''} ${f.key === 'approval' ? 'is-approval' : ''}`}
                                onClick={() => setFilter(f.key)}
                            >
                                {f.label}{n > 0 ? <span className="msgr-chip-count">{n}</span> : null}
                            </button>
                        );
                    })}
                </div>

                <div className="msgr-tasks-meta">
                    <span>
                        {total == null ? '' : `${total} task${total === 1 ? '' : 's'}`}
                        {search && total != null ? ` matching “${search}”` : ''}
                    </span>
                    <span className="msgr-tasks-sort">
                        Sort:
                        {SORTS.map((s) => (
                            <button
                                key={s.key}
                                type="button"
                                className={sort === s.key ? 'active' : ''}
                                onClick={() => setSort(s.key)}
                            >
                                {s.label}
                            </button>
                        ))}
                    </span>
                </div>

                <div className="msgr-tasks-list" ref={listRef} onScroll={onScroll}>
                    {items.map((t) => (
                        <ChatTaskCard
                            key={t._id}
                            task={t}
                            onOpen={(task) => setOpenTask(task)}
                            onApprove={approve}
                            busy={deciding === t._id}
                        />
                    ))}

                    {loading && (
                        <div className="msgr-empty">
                            <FontAwesomeIcon icon={faSpinner} className="msgr-history-spinner" /> Loading…
                        </div>
                    )}
                    {error && (
                        <div className="msgr-empty">
                            {error}
                            <button type="button" className="msgr-btn" onClick={() => load(items.length ? page + 1 : 1)}>
                                Retry
                            </button>
                        </div>
                    )}
                    {!loading && !error && !items.length && (
                        <div className="msgr-empty">
                            {search || counts?.all ? 'No tasks match.'
                                : scope === 'between' ? `No tasks between you and ${name} yet.` : `${name} has no tasks yet.`}
                        </div>
                    )}
                    {/* For when the first page does not fill the panel, so there is nothing to scroll. */}
                    {!loading && !error && hasMore && (
                        <button type="button" className="msgr-btn block" onClick={loadMore}>Load more</button>
                    )}
                </div>
            </aside>

            {openTask && (
                <ChatTaskDrawer
                    taskId={openTask._id}
                    myRole={myRole}
                    canDecide={Boolean(openTask.canApprove)}
                    onDecided={(decision) => decided(openTask._id, decision)}
                    focusDiscussion={openTask.unreadComments > 0}
                    onClose={() => { read(openTask); setOpenTask(null); }}
                />
            )}
        </>
    );
};

export default ChatTasksPanel;
